import { msg, Trans } from '@lingui/macro'

import * as React from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { LinguiMessageDescriptor, useTrans } from '~logic/i18n/useTrans'
import { InputControl } from '~ui/components/formElements/accessories/InputControl'
import { InputGroup } from '~ui/components/formElements/accessories/InputGroup'
import { InputLabel } from '~ui/components/formElements/accessories/InputLabel'
import {
  InputErrorMessage,
  InputHelperMessage,
} from '~ui/components/formElements/accessories/InputMessages'
import { Input } from '~ui/components/formElements/Input/Input'

import { FORM_INPUT_RADIUS } from '../config/form.config'

type TNoteForm = { note: string }

type TProps = NoChildren & {
  label?: LinguiMessageDescriptor
  placeholder?: LinguiMessageDescriptor
  isRequired?: boolean
}

const NOTE_LINES = 5
const MAX_NOTE_CHAR_LENGTH = 240 // max. tweet length

/**
 * An input for the order note.
 * We have notes on each order item as well so this could serve
 * as a more general note (eg. for stuff related to the whole order or meant for a driver).
 *
 * Different label can be used via the `label` prop, defaults to _Note_ (translated).
 * Different placeholder can be used via the `placeholder` prop.
 *
 * ! Needs the `FormProvider` from 'react-hook-form' to be rendered somewhere above,
 * see {@link https://react-hook-form.com/api/formprovider} for examples and details.
 */
export const NoteInput: React.FC<TProps> = ({
  label = msg`Note`,
  placeholder,
  isRequired = false,
}) => {
  const { trans } = useTrans()
  const { control } = useFormContext<TNoteForm>()

  return (
    <InputGroup>
      <InputControl isRequired={isRequired}>
        <InputLabel>{trans(label)}</InputLabel>
        <Controller
          name={`note`}
          control={control}
          rules={{
            required: { value: isRequired, message: trans(msg`Please enter feedback`) },
            maxLength: {
              value: MAX_NOTE_CHAR_LENGTH,
              message: trans(msg`The note is too long (max. ${MAX_NOTE_CHAR_LENGTH} characters)`),
            },
          }}
          render={({
            field: { onChange, onBlur, value, name },
            fieldState: { invalid, error },
            formState: { isSubmitting },
          }) => (
            <>
              <Input
                id={name}
                value={value}
                onBlur={onBlur}
                onChange={onChange}
                isInvalid={invalid}
                autoComplete={`none`}
                isDisabled={isSubmitting}
                numberOfLines={NOTE_LINES}
                radius={FORM_INPUT_RADIUS}
                placeholder={placeholder ? trans(placeholder) : undefined}
              />
              {invalid && error?.message ? (
                <InputErrorMessage>{error.message}</InputErrorMessage>
              ) : (
                <InputHelperMessage>
                  <Trans>Maximum {MAX_NOTE_CHAR_LENGTH} characters</Trans>
                </InputHelperMessage>
              )}
            </>
          )}
        />
      </InputControl>
    </InputGroup>
  )
}
